import '@/lib/initDatadog';
import { initSocialEmbed } from '@/lib/initSocialEmbed';
import { useLenis } from '@studio-freight/react-lenis';
import { useEffect, useRef, useState } from 'react';
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query';
import { PreLoader } from '@/components/global/PreLoader';
import { AwinMasterTag, CookieConsent, ZendeskChatbot } from '@/components/tags';
import { determinei18nLocale } from '@/helpers/determineLocale';
import { useForceHtmlLangAttribute } from '@/helpers/forceHtmlLang';
import { getBaseUrl } from '@/helpers/getBaseUrl';
import { ILocales } from '@/interfaces/locales.interfaces';
import client from '@/lib/apolloClient';
import * as gtm from '@/lib/gtm';
import { jucyAttribution } from '@/lib/jucyAttribution';
import { ApolloProvider } from '@apollo/client';
import { LazyMotion, domAnimation } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger';
import { NextIntlProvider } from 'next-intl';
import { OrganizationJsonLd, SocialProfileJsonLd } from 'next-seo';
import { AppProps } from 'next/app';
import { Poppins } from 'next/font/google';
import { useRouter } from 'next/router';
import Script from 'next/script';
import NextNProgress from 'nextjs-progressbar';
import '../styles/globals.css';

jucyAttribution.init();

export const poppins = Poppins({
    subsets: ['latin'],
    weight: ['300', '400', '500', '600', '700'],
});

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.defaults({ markers: process.env.NODE_ENV === 'development' });

    // merge rafs
    gsap.ticker.lagSmoothing(0);
    gsap.ticker.remove(gsap.updateRoot);

    // reset scroll position
    window.scrollTo(0, 0);
    window.history.scrollRestoration = 'manual';
}

const baseUrl = getBaseUrl();

export const queryClient = new QueryClient({
    defaultOptions: { queries: { staleTime: Infinity } },
});

const MyApp = ({ Component, pageProps }: AppProps) => {
    const isFirstLoad = useRef<boolean>(true);
    const [loading, setLoading] = useState(false);
    const router = useRouter();

    useEffect(() => {
        if (isFirstLoad.current) {
            gtm.pageview(`/${router.locale}${router.asPath}`);
            isFirstLoad.current = false;
        }
    }, [router.locale, router.asPath]);

    const lenis = useLenis(ScrollTrigger.update);
    useEffect(() => {
        ScrollTrigger.refresh;
    }, [lenis]);

    const handleRouteChange = (url: string) => {
        gtm.pageview(url);
        setLoading(false);
        initSocialEmbed();
    };

    useEffect(() => {
        router.events.on('routeChangeStart', () => setLoading(true));
        router.events.on('routeChangeComplete', handleRouteChange);
        router.events.on('routeChangeError', () => setLoading(false));

        return () => {
            router.events.off('routeChangeStart', () => setLoading(true));
            router.events.off('routeChangeComplete', handleRouteChange);
            router.events.off('routeChangeError', () => setLoading(false));
        };
    }, [router.events]);

    useForceHtmlLangAttribute(determinei18nLocale(router.locale as ILocales));

    return (
        <>
            <AwinMasterTag awinId="114370" />
            <CookieConsent />
            <ZendeskChatbot />
            <Script src="https://cdn.jsdelivr.net/npm/@mux/mux-player"></Script>
            {/* https://nextjs.org/docs/pages/building-your-application/optimizing/fonts#apply-the-font-in-head */}
            {/* eslint-disable-next-line react/no-unknown-property */}
            <style jsx global>
                {`
                    html {
                        font-family: ${poppins.style.fontFamily};
                    }
                `}
            </style>
            <noscript>
                <iframe src={`https://www.googletagmanager.com/ns.html?id=${gtm.GTM_ID}`} height="0" width="0" style={{ display: 'none', visibility: 'hidden' }} />
            </noscript>
            <Script
                id="gtm-script"
                strategy="afterInteractive"
                dangerouslySetInnerHTML={{
                    __html: `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    '/metrics/?id='+i+dl+'&gtm_auth=${gtm.GTM_AUTH}&gtm_preview=${gtm.GTM_PREVIEW}&gtm_cookies_win=x';f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer', '${gtm.GTM_ID}');
  `,
                }}
            />
            <NextIntlProvider>
                <ApolloProvider client={client}>
                    <QueryClientProvider client={queryClient}>
                        <Hydrate state={pageProps.dehydratedState}>
                            <NextNProgress />
                            <LazyMotion features={domAnimation}>
                                {loading ? (
                                    <PreLoader />
                                ) : (
                                    <main>
                                        <Component {...pageProps} />
                                    </main>
                                )}
                            </LazyMotion>
                        </Hydrate>
                    </QueryClientProvider>
                </ApolloProvider>
            </NextIntlProvider>
            <OrganizationJsonLd
                type="Corporation"
                name="Star RV"
                legalName="Star RV Limited"
                address={{
                    streetAddress: '9 Richard Pearse Drive',
                    addressLocality: 'Māngere',
                    addressRegion: 'Auckland',
                    postalCode: '2022',
                    addressCountry: 'NZ',
                }}
                contactPoint={[
                    {
                        telephone: '+64 9 600 0805',
                        contactType: 'customer service',
                        email: 'res@starrv.com',
                        availableLanguage: 'English',
                    },
                    {
                        telephone: '+64 9 374 4360',
                        contactType: 'customer service',
                        email: 'res@starrv.com',
                        areaServed: 'NZ',
                        contactOption: 'TollFree',
                        availableLanguage: 'English',
                    },
                    {
                        telephone: '+64 9 600 1032',
                        contactType: 'customer service',
                        email: 'res@starrv.com',
                        areaServed: 'AU',
                        contactOption: 'TollFree',
                        availableLanguage: 'English',
                    },
                ]}
                url={baseUrl}
                logo={`${baseUrl}/default-star-logo.png`}
            />
            <SocialProfileJsonLd
                type="Organization"
                name="Star RV"
                url={baseUrl}
                sameAs={['https://www.facebook.com/StarRVWorld', 'https://www.youtube.com/@starrvtv', 'https://www.instagram.com/star_rv_world']}
            />
        </>
    );
};

export default MyApp;
